/**
 * The `circle.json` manifest: one definition shared by the backend (validator),
 * the CLI and the builder (validateManifest) and the runtime (types).
 */
import { v, type Infer } from 'convex/values';
import { checkManifestConnectors } from './connectors';
import { isValidSlug, slugify } from './slug';
import { parseHHMM } from './time';

/** `server`: only the app's server code reads or writes it; clients see that it exists (`runtime.context`), never its documents. */
export type CollectionScope = 'shared' | 'mine' | 'server';
export type WritableBy = 'all' | 'adults';
export type PatchableBy = 'anyone' | 'creator';

/** One entry of `collections`. The rules are enforced by `lib/rules.ts#checkRules`. */
export type CollectionSpec = {
  scope: CollectionScope;
  /** Who may put/patch/remove: everyone (default) or only members with `kid: false`. */
  writableBy?: WritableBy;
  /** Who may patch/remove an existing document. `mine` is creator-only already, so only "creator" is accepted there. */
  patchableBy?: PatchableBy;
};

/** The two file names a bundle's server code may start from. */
export const SERVER_ENTRIES = ['server/index.ts', 'server/index.js'] as const;
export type ServerEntry = (typeof SERVER_ENTRIES)[number];

export type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

/** One entry of `server.schedules`: `onSchedule({ name, args })` fires at `at` in the circle's time zone. */
export type ServerScheduleSpec = {
  every: 'day' | 'week';
  /** 'HH:MM' (24h), circle time zone. */
  at: string;
  /** 0 = Sunday … 6 = Saturday; required for `every: 'week'`, refused otherwise. */
  weekday?: number;
  /** Passed to `onSchedule` as `args`. JSON, ≤ 4 KB serialised. */
  args?: JsonValue;
};

/**
 * The `server` block. Optional and implied: a bundle with `server/index.ts` (or `.js`) has server code
 * whether or not the block is there; the CLI fills in `entry` when it sends one.
 */
export type ServerSpec = {
  /** Which file the server code starts from. Default: whichever of the two exists. */
  entry?: ServerEntry;
  /** Named schedules for `onSchedule` (slug names, ≤ 10). */
  schedules?: Record<string, ServerScheduleSpec>;
  /** Slugs of inbound webhooks for `onWebhook` (unique, ≤ 10). */
  webhooks?: string[];
};

export type CircleManifest = {
  /** Shown on the launcher tile and in "<sender> sent <name>". 1–60 chars. */
  name: string;
  /** URL segment under /c/<circle>/. Defaults to slugify(name). Unique per circle. */
  slug?: string;
  /** An emoji, or a bundle-relative path such as "icon.png". */
  icon?: string;
  /** One plain sentence on what the app does for the circle, shown on its card in the builder. 1–140 chars. */
  description?: string;
  /** Collections the app uses, with their scope and write rules. Undeclared collections are rejected server-side. */
  collections?: Record<string, CollectionSpec>;
  /** Declares that the app calls notify/schedule. Informational in v0. */
  push?: boolean;
  /** Names of the circle's connectors the app calls (`lib/connectors.ts`). `apps.publish` warns about unconfigured ones. */
  connectors?: string[];
  /** Server code (`server/index.ts|js`): schedules and webhooks it handles. Implied by the entry file. */
  server?: ServerSpec;
  /** Free-form, shown in the builder's version list. */
  version?: string;
};

export const collectionSpecValidator = v.object({
  scope: v.union(v.literal('shared'), v.literal('mine'), v.literal('server')),
  writableBy: v.optional(v.union(v.literal('all'), v.literal('adults'))),
  patchableBy: v.optional(v.union(v.literal('anyone'), v.literal('creator'))),
});

export const serverSpecValidator = v.object({
  entry: v.optional(v.union(v.literal('server/index.ts'), v.literal('server/index.js'))),
  schedules: v.optional(
    v.record(
      v.string(),
      v.object({
        every: v.union(v.literal('day'), v.literal('week')),
        at: v.string(),
        weekday: v.optional(v.number()),
        args: v.optional(v.any()),
      }),
    ),
  ),
  webhooks: v.optional(v.array(v.string())),
});

export const manifestValidator = v.object({
  name: v.string(),
  slug: v.optional(v.string()),
  icon: v.optional(v.string()),
  description: v.optional(v.string()),
  collections: v.optional(v.record(v.string(), collectionSpecValidator)),
  push: v.optional(v.boolean()),
  connectors: v.optional(v.array(v.string())),
  server: v.optional(serverSpecValidator),
  version: v.optional(v.string()),
});

// Keep the hand-written type and the validator in sync at compile time.
type _Check = Infer<typeof manifestValidator> extends CircleManifest
  ? CircleManifest extends Infer<typeof manifestValidator>
    ? true
    : never
  : never;
const _check: _Check = true;
void _check;

export const COLLECTION_NAME_RE = /^[a-zA-Z][a-zA-Z0-9_-]{0,39}$/;
export const MAX_NAME_LENGTH = 60;
export const MAX_DESCRIPTION_LENGTH = 140;
const COLLECTION_KEYS = new Set(['scope', 'writableBy', 'patchableBy']);
const SERVER_KEYS = new Set(['entry', 'schedules', 'webhooks']);
const SCHEDULE_KEYS = new Set(['every', 'at', 'weekday', 'args']);
export const MAX_SERVER_SCHEDULES = 10;
export const MAX_SERVER_WEBHOOKS = 10;
export const MAX_SCHEDULE_ARGS_BYTES = 4096;

export type ManifestValidation =
  | { ok: true; manifest: CircleManifest }
  | { ok: false; errors: string[] };

/**
 * What the caller knows about the bundle's server code, for the checks `circle.json` alone cannot make:
 * - a `ServerEntry`: the bundle has that entry file (a declared `server.entry` must match; it is filled in);
 * - `true`: the send carries a server script, file name unknown (`apps.publish`);
 * - `false`: no server code (`server.*` and `scope: "server"` are refused);
 * - left out: shape only.
 */
export type ManifestOptions = { server?: ServerEntry | boolean };

function isPlainObject(x: unknown): x is Record<string, unknown> {
  return typeof x === 'object' && x !== null && !Array.isArray(x);
}

function isJson(x: unknown, depth = 0): boolean {
  if (depth > 20) return false;
  if (x === null || typeof x === 'string' || typeof x === 'boolean') return true;
  if (typeof x === 'number') return Number.isFinite(x);
  if (Array.isArray(x)) return x.every((item) => isJson(item, depth + 1));
  if (isPlainObject(x)) return Object.values(x).every((item) => isJson(item, depth + 1));
  return false;
}

/** Why `args` cannot be a schedule's `args` (circle.json `server.schedules`, a `call` schedule), or null: plain JSON, ≤ 4 KB serialised. */
export function scheduleArgsProblem(args: unknown): string | null {
  if (!isJson(args)) return '"args" must be plain JSON';
  if (new TextEncoder().encode(JSON.stringify(args)).byteLength > MAX_SCHEDULE_ARGS_BYTES) {
    return `"args" must be at most ${MAX_SCHEDULE_ARGS_BYTES} bytes of JSON`;
  }
  return null;
}

const NO_SERVER_CODE = 'needs server code: add server/index.ts (or server/index.js) next to index.html';

function checkServerSpec(input: unknown, server: ManifestOptions['server'], errors: string[]): ServerSpec | undefined {
  if (!isPlainObject(input)) {
    errors.push('"server" must be an object of { entry?, schedules?, webhooks? }');
    return undefined;
  }
  const out: ServerSpec = {};
  const extra = Object.keys(input).filter((k) => !SERVER_KEYS.has(k));
  if (extra.length > 0) errors.push(`"server": unknown keys ${extra.join(', ')} (allowed: entry, schedules, webhooks)`);

  if (input.entry !== undefined) {
    if (!SERVER_ENTRIES.includes(input.entry as ServerEntry)) {
      errors.push(`"server.entry" must be "server/index.ts" or "server/index.js" (or left out: the file that exists is used)`);
    } else if (server === false) {
      errors.push(`"server.entry" is "${input.entry}" but the bundle has no such file`);
    } else if (typeof server === 'string' && server !== input.entry) {
      errors.push(`"server.entry" is "${input.entry}" but the bundle has ${server}`);
    } else {
      out.entry = input.entry as ServerEntry;
    }
  }

  if (input.schedules !== undefined) {
    if (!isPlainObject(input.schedules)) {
      errors.push('"server.schedules" must be an object of { <name>: { "every": "day" | "week", "at": "HH:MM", "weekday"?, "args"? } }');
    } else {
      const entries = Object.entries(input.schedules);
      if (server === false && entries.length > 0) errors.push(`"server.schedules" ${NO_SERVER_CODE}`);
      if (entries.length > MAX_SERVER_SCHEDULES) errors.push(`"server.schedules" may name at most ${MAX_SERVER_SCHEDULES} schedules`);
      const schedules: Record<string, ServerScheduleSpec> = {};
      for (const [name, spec] of entries) {
        const where = `server schedule "${name}"`;
        if (!isValidSlug(name)) {
          errors.push(`${where}: name must be 1–40 lowercase letters, digits and hyphens`);
          continue;
        }
        if (!isPlainObject(spec)) {
          errors.push(`${where}: must be { "every": "day" | "week", "at": "HH:MM" }`);
          continue;
        }
        const unknown = Object.keys(spec).filter((k) => !SCHEDULE_KEYS.has(k));
        if (unknown.length > 0) errors.push(`${where}: unknown keys ${unknown.join(', ')} (allowed: every, at, weekday, args)`);
        const before = errors.length;
        if (spec.every !== 'day' && spec.every !== 'week') errors.push(`${where}: "every" must be "day" or "week"`);
        if (typeof spec.at !== 'string' || !parseHHMM(spec.at)) errors.push(`${where}: "at" must be "HH:MM" (24h, the circle's time zone)`);
        if (spec.every === 'week') {
          if (typeof spec.weekday !== 'number' || !Number.isInteger(spec.weekday) || spec.weekday < 0 || spec.weekday > 6) {
            errors.push(`${where}: "weekday" (0–6, 0 = Sunday) is required with "every": "week"`);
          }
        } else if (spec.weekday !== undefined) {
          errors.push(`${where}: "weekday" only goes with "every": "week"`);
        }
        if (spec.args !== undefined) {
          const problem = scheduleArgsProblem(spec.args);
          if (problem) errors.push(`${where}: ${problem}`);
        }
        if (errors.length > before || unknown.length > 0) continue;
        const out: ServerScheduleSpec = { every: spec.every as 'day' | 'week', at: spec.at as string };
        if (spec.weekday !== undefined) out.weekday = spec.weekday as number;
        if (spec.args !== undefined) out.args = spec.args as JsonValue;
        schedules[name] = out;
      }
      if (entries.length > 0) out.schedules = schedules;
    }
  }

  if (input.webhooks !== undefined) {
    if (!Array.isArray(input.webhooks)) {
      errors.push('"server.webhooks" must be an array of webhook names, e.g. ["stripe"]');
    } else {
      if (server === false && input.webhooks.length > 0) errors.push(`"server.webhooks" ${NO_SERVER_CODE}`);
      if (input.webhooks.length > MAX_SERVER_WEBHOOKS) errors.push(`"server.webhooks" may name at most ${MAX_SERVER_WEBHOOKS} webhooks`);
      const seen = new Set<string>();
      for (const name of input.webhooks) {
        if (typeof name !== 'string' || !isValidSlug(name)) {
          errors.push(`server webhook ${JSON.stringify(name)}: must be 1–40 lowercase letters, digits and hyphens`);
        } else if (seen.has(name)) {
          errors.push(`server webhook "${name}" is listed twice`);
        } else {
          seen.add(name);
        }
      }
      if (input.webhooks.length > 0) out.webhooks = [...seen];
    }
  }

  if (typeof server === 'string') out.entry = server;
  return out;
}

/**
 * Validates an unknown value as a circle.json manifest and normalises it
 * (fills in `slug`, and `server.entry` when `options.server` names the entry file).
 * Pure TypeScript so the CLI and the builder can run it before uploading;
 * `apps.publish` runs it again on the server.
 */
export function validateManifest(input: unknown, options: ManifestOptions = {}): ManifestValidation {
  const errors: string[] = [];
  if (!isPlainObject(input)) {
    return { ok: false, errors: ['circle.json must be a JSON object'] };
  }

  const name = input.name;
  if (typeof name !== 'string' || name.trim().length === 0) {
    errors.push('"name" is required and must be a non-empty string');
  } else if (name.trim().length > MAX_NAME_LENGTH) {
    errors.push(`"name" must be at most ${MAX_NAME_LENGTH} characters`);
  }

  let slug: string | undefined;
  if (input.slug !== undefined) {
    if (typeof input.slug !== 'string') {
      errors.push('"slug" must be a string');
    } else if (!isValidSlug(input.slug)) {
      errors.push('"slug" must be 1–40 chars of lowercase letters, digits and hyphens, not starting or ending with a hyphen');
    } else {
      slug = input.slug;
    }
  } else if (typeof name === 'string') {
    slug = slugify(name);
    if (!isValidSlug(slug)) {
      errors.push('"name" does not produce a valid slug; set "slug" explicitly');
    }
  }

  let icon: string | undefined;
  if (input.icon !== undefined) {
    if (typeof input.icon !== 'string' || input.icon.length === 0 || input.icon.length > 200) {
      errors.push('"icon" must be a non-empty string (an emoji or a bundle-relative path)');
    } else {
      icon = input.icon;
    }
  }

  let description: string | undefined;
  if (input.description !== undefined) {
    if (typeof input.description !== 'string' || input.description.trim().length === 0) {
      errors.push('"description" must be a non-empty string');
    } else if (input.description.trim().length > MAX_DESCRIPTION_LENGTH) {
      errors.push(`"description" must be at most ${MAX_DESCRIPTION_LENGTH} characters`);
    } else {
      description = input.description.trim();
    }
  }

  let collections: Record<string, CollectionSpec> | undefined;
  if (input.collections !== undefined) {
    if (!isPlainObject(input.collections)) {
      errors.push('"collections" must be an object of { <name>: { scope: "shared" | "mine" | "server", writableBy?, patchableBy? } }');
    } else {
      collections = {};
      for (const [collectionName, spec] of Object.entries(input.collections)) {
        if (!COLLECTION_NAME_RE.test(collectionName)) {
          errors.push(`collection "${collectionName}": name must match ${COLLECTION_NAME_RE}`);
          continue;
        }
        if (!isPlainObject(spec) || (spec.scope !== 'shared' && spec.scope !== 'mine' && spec.scope !== 'server')) {
          errors.push(`collection "${collectionName}": scope must be "shared", "mine" or "server"`);
          continue;
        }
        const extra = Object.keys(spec).filter((k) => !COLLECTION_KEYS.has(k));
        if (extra.length > 0) {
          errors.push(`collection "${collectionName}": unknown keys ${extra.join(', ')}`);
          continue;
        }
        if (spec.scope === 'server') {
          if (spec.writableBy !== undefined || spec.patchableBy !== undefined) {
            errors.push(`collection "${collectionName}": writableBy / patchableBy do not apply to a "server" collection (only the app's server code reads and writes it)`);
            continue;
          }
          if (options.server === false) {
            errors.push(`collection "${collectionName}": scope "server" ${NO_SERVER_CODE}`);
            continue;
          }
        }
        const out: CollectionSpec = { scope: spec.scope };
        if (spec.writableBy !== undefined) {
          if (spec.writableBy !== 'all' && spec.writableBy !== 'adults') {
            errors.push(`collection "${collectionName}": writableBy must be "all" or "adults"`);
          } else {
            out.writableBy = spec.writableBy;
          }
        }
        if (spec.patchableBy !== undefined) {
          if (spec.patchableBy !== 'anyone' && spec.patchableBy !== 'creator') {
            errors.push(`collection "${collectionName}": patchableBy must be "anyone" or "creator"`);
          } else if (spec.scope === 'mine' && spec.patchableBy === 'anyone') {
            errors.push(`collection "${collectionName}": patchableBy "anyone" is not possible on a "mine" collection (only the creator sees its documents)`);
          } else {
            out.patchableBy = spec.patchableBy;
          }
        }
        collections[collectionName] = out;
      }
    }
  }

  let push: boolean | undefined;
  if (input.push !== undefined) {
    if (typeof input.push !== 'boolean') errors.push('"push" must be a boolean');
    else push = input.push;
  }

  let connectors: string[] | undefined;
  if (input.connectors !== undefined) {
    const checked = checkManifestConnectors(input.connectors);
    if (checked.ok) connectors = checked.value;
    else errors.push(...checked.errors);
  }

  let server: ServerSpec | undefined;
  if (input.server !== undefined) {
    server = checkServerSpec(input.server, options.server, errors);
  } else if (typeof options.server === 'string') {
    server = { entry: options.server };
  }
  if (server && Object.keys(server).length === 0) server = undefined;

  let version: string | undefined;
  if (input.version !== undefined) {
    if (typeof input.version !== 'string' || input.version.length > 60) {
      errors.push('"version" must be a string of at most 60 characters');
    } else {
      version = input.version;
    }
  }

  const known = new Set(['name', 'slug', 'icon', 'description', 'collections', 'push', 'connectors', 'server', 'version']);
  for (const key of Object.keys(input)) {
    if (!known.has(key)) errors.push(`unknown key "${key}"`);
  }

  if (errors.length > 0) return { ok: false, errors };

  const manifest: CircleManifest = { name: (name as string).trim(), slug: slug as string };
  if (icon !== undefined) manifest.icon = icon;
  if (description !== undefined) manifest.description = description;
  if (collections !== undefined) manifest.collections = collections;
  if (push !== undefined) manifest.push = push;
  if (connectors !== undefined) manifest.connectors = connectors;
  if (server !== undefined) manifest.server = server;
  if (version !== undefined) manifest.version = version;
  return { ok: true, manifest };
}
